/**
 * Poseidon dashboard — Live overview (§3.2 A).
 *
 * The primary screen: today's KPI cards, the approval queue, open positions,
 * working orders, recent closed trades, active signals, feed freshness and the
 * most recent system events.
 *
 * The status ribbon and the last-data-timestamp indicator are rendered by the
 * shell, so they are present here without this page having to remember them.
 */
import { Link } from 'react-router-dom';
import type { ClosedTradeRow, PositionRow, SignalRow, TradeFilters, WorkingOrderRow } from '../api/types';
import { POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useDashboardEvent, useServices } from '../state/Services';
import { CsvExportButton, DataTable } from '../components/DataTable';
import type { TableColumn } from '../components/DataTable';
import { KpiGrid } from '../components/KpiGrid';
import { FreshnessMeter } from '../components/Staleness';
import { ApproveRejectIntentControls, CancelOrderControl, ClosePositionControl } from '../components/Controls';
import { SystemEventList } from '../components/SystemEventList';
import { Badge, ErrorState, LoadingState, Panel, Unavailable } from '../components/primitives';
import { useRibbon } from '../components/AppShell';
import {
  EXIT_REASON_LABELS,
  OUTCOME_LABELS,
  RESEARCH_STATUS_LABELS,
  VERDICT_LABELS,
  toneForOutcome,
  toneForResearchStatus,
  toneForVerdict,
} from '../lib/labels';
import { formatDateTimeWithZone, formatDuration, formatNumber, formatPercent, formatPrice, formatSigned } from '../lib/format';

const EMPTY_TRADE_FILTERS: TradeFilters = {
  from: null,
  to: null,
  sessions: [],
  strategies: [],
  symbols: [],
  outcomes: [],
  exit_reasons: [],
};

export function LiveOverview(): JSX.Element {
  const { api, isMock, resyncToken } = useServices();
  const ribbon = useRibbon();

  const kpis = useApiData('overview-kpis', () => api.getKpis(), { pollMs: POLL_INTERVALS.kpis, mode: api.mode });
  const positions = useApiData('overview-positions', () => api.getPositions(), { pollMs: POLL_INTERVALS.positions, mode: api.mode });
  const orders = useApiData('overview-orders', () => api.getWorkingOrders(), { pollMs: POLL_INTERVALS.orders, mode: api.mode });
  const trades = useApiData('overview-trades', () => api.getClosedTrades(EMPTY_TRADE_FILTERS), {
    pollMs: POLL_INTERVALS.kpis,
    mode: api.mode,
  });
  const signals = useApiData('overview-signals', () => api.getSignals(), { pollMs: POLL_INTERVALS.signals, mode: api.mode });
  const events = useApiData('overview-events', () => api.getSystemEvents(), { pollMs: POLL_INTERVALS.events, mode: api.mode });

  // Live deltas: a closed trade changes the ledger, the KPIs and the positions,
  // so refetch those snapshots rather than trying to patch them locally.
  useDashboardEvent('trade.closed', () => {
    trades.refresh();
    kpis.refresh();
    positions.refresh();
  });
  useDashboardEvent('position.updated', () => positions.refresh());
  useDashboardEvent('order.updated', () => orders.refresh());
  useDashboardEvent('signal.new', () => signals.refresh());
  useDashboardEvent('system.event', () => events.refresh());

  const approvalQueue: SignalRow[] = (signals.data ?? []).filter((candidate) => candidate.status === 'awaiting_approval');
  const recentTrades = (trades.data?.items ?? []).slice(0, 8);

  const positionColumns: readonly TableColumn<PositionRow, 'symbol' | 'side' | 'lots' | 'entry' | 'mark' | 'pnl' | 'age' | 'risk' | 'actions'>[] = [
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
    { key: 'side', header: 'Side', sortValue: (row) => row.side, render: (row) => <Badge tone={row.side === 'buy' ? 'info' : 'warn'}>{row.side}</Badge> },
    { key: 'lots', header: 'Lots', align: 'right', sortValue: (row) => row.lots, render: (row) => formatNumber(row.lots, 2) },
    { key: 'entry', header: 'Entry', align: 'right', sortValue: (row) => row.entry_price, render: (row) => formatPrice(row.entry_price) },
    {
      key: 'mark',
      header: 'Bid / ask',
      align: 'right',
      render: (row) => (
        <span className="text-mono">
          {formatPrice(row.latest_bid)} / {formatPrice(row.latest_ask)}
          {row.quote_age_ms !== null && <span className="text-faint"> ({formatDuration(row.quote_age_ms)})</span>}
        </span>
      ),
    },
    {
      key: 'pnl',
      header: 'Unrealized',
      align: 'right',
      sortValue: (row) => row.unrealized_pnl,
      render: (row) => <span className={row.unrealized_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatSigned(row.unrealized_pnl)}</span>,
    },
    {
      key: 'age',
      header: 'Stop / target',
      render: (row) => (
        <>
          <div className="text-mono">
            {formatPrice(row.stop_loss)} / {formatPrice(row.take_profit)}
          </div>
          {!row.stop_confirmed_at_broker && <span className="text-warn" style={{ fontSize: 11 }}>stop not resting at broker</span>}
        </>
      ),
    },
    {
      key: 'risk',
      header: 'Risk / age / decision',
      render: (row) => (
        <>
          <Badge tone={row.risk_status === 'protected' ? 'ok' : row.risk_status === 'unprotected_stop' ? 'danger' : 'warn'}>{row.risk_status.replace(/_/g, ' ')}</Badge>
          <div className="text-faint" style={{ fontSize: 11 }}>
            {formatDuration(row.age_ms)} · <span className="text-mono">{row.strategy_version}</span>
          </div>
          <div className="text-faint text-mono" style={{ fontSize: 10.5 }}>
            {row.decision_id}
          </div>
        </>
      ),
    },
    {
      key: 'actions',
      header: 'Close',
      render: (row) => <ClosePositionControl position={row} haltLevel={ribbon?.halt.level ?? 'none'} />,
    },
  ];

  const orderColumns: readonly TableColumn<WorkingOrderRow, 'ticket' | 'symbol' | 'state' | 'lots' | 'price' | 'age' | 'actions'>[] = [
    { key: 'ticket', header: 'Ticket', render: (row) => <span className="text-mono">{row.order_ticket}</span> },
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => `${row.symbol} ${row.side}` },
    { key: 'state', header: 'State', sortValue: (row) => row.order_state, render: (row) => <Badge tone={row.order_state === 'rejected' ? 'danger' : row.order_state === 'partially_filled' ? 'warn' : 'info'}>{row.order_state.replace(/_/g, ' ')}</Badge> },
    { key: 'lots', header: 'Lots (filled)', align: 'right', sortValue: (row) => row.lots, render: (row) => `${formatNumber(row.lots, 2)} (${formatNumber(row.filled_lots, 2)})` },
    { key: 'price', header: 'Requested', align: 'right', sortValue: (row) => row.requested_price, render: (row) => formatPrice(row.requested_price) },
    { key: 'age', header: 'Age / deviation', sortValue: (row) => row.age_ms, render: (row) => `${formatDuration(row.age_ms)} · ${row.deviation_points ?? '—'} pts` },
    { key: 'actions', header: 'Cancellation', render: (row) => <CancelOrderControl order={row} /> },
  ];

  const tradeColumns: readonly TableColumn<ClosedTradeRow, 'id' | 'symbol' | 'direction' | 'times' | 'net' | 'r' | 'outcome' | 'strategy' | 'confidence'>[] = [
    { key: 'id', header: 'Trade', render: (row) => <Link to={`/trades/${row.completed_trade_id}`} className="text-mono">{row.completed_trade_id}</Link> },
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
    { key: 'direction', header: 'Direction', sortValue: (row) => row.direction, render: (row) => row.direction },
    {
      key: 'times',
      header: 'Entry → exit',
      sortValue: (row) => row.exit_timestamp,
      render: (row) => (
        <span className="text-mono" style={{ fontSize: 11.5 }}>
          {formatDateTimeWithZone(row.entry_timestamp)} → {formatDateTimeWithZone(row.exit_timestamp)}
          <div className="text-faint">{formatDuration(row.holding_time_ms)}</div>
        </span>
      ),
    },
    { key: 'net', header: 'Net P&L', align: 'right', sortValue: (row) => row.net_realized_pnl, render: (row) => <span className={row.net_realized_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatSigned(row.net_realized_pnl)}</span> },
    { key: 'r', header: 'R', align: 'right', sortValue: (row) => row.r_multiple, render: (row) => (row.r_multiple === null ? <Unavailable /> : `${formatNumber(row.r_multiple, 2)}R`) },
    { key: 'outcome', header: 'Outcome', sortValue: (row) => row.outcome, render: (row) => <Badge tone={toneForOutcome(row.outcome)}>{OUTCOME_LABELS[row.outcome]}</Badge> },
    { key: 'strategy', header: 'Strategy', sortValue: (row) => row.strategy_version, render: (row) => <span className="text-mono">{row.strategy_version}</span> },
    { key: 'confidence', header: 'Research conf.', align: 'right', sortValue: (row) => row.research_confidence, render: (row) => (row.research_confidence === null ? <Unavailable /> : formatPercent(row.research_confidence)) },
  ];

  const signalColumns: readonly TableColumn<SignalRow, 'symbol' | 'side' | 'score' | 'confidence' | 'research' | 'verdict' | 'status'>[] = [
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => `${row.symbol} ${row.side}` },
    { key: 'side', header: 'Strategy', sortValue: (row) => row.strategy_version, render: (row) => <span className="text-mono">{row.strategy_version}</span> },
    { key: 'score', header: 'Quant → final', align: 'right', sortValue: (row) => row.final_score, render: (row) => <span className="text-mono">{formatNumber(row.quant_score, 3)} → {row.final_score === null ? '—' : formatNumber(row.final_score, 3)}</span> },
    { key: 'confidence', header: 'Confidence', align: 'right', sortValue: (row) => row.model_confidence, render: (row) => formatNumber(row.model_confidence, 3) },
    { key: 'research', header: 'Research', sortValue: (row) => row.research_status, render: (row) => <Badge tone={toneForResearchStatus(row.research_status)}>{RESEARCH_STATUS_LABELS[row.research_status]}</Badge> },
    { key: 'verdict', header: 'Risk verdict', sortValue: (row) => row.risk_verdict, render: (row) => (row.risk_verdict === null ? <span className="text-faint">not yet evaluated</span> : <Badge tone={toneForVerdict(row.risk_verdict)}>{VERDICT_LABELS[row.risk_verdict]}</Badge>) },
    {
      key: 'status',
      header: 'Status / rejection reason',
      sortValue: (row) => row.status,
      render: (row) => (
        <>
          <div>{row.status.replace(/_/g, ' ')}</div>
          {row.rejection_reason !== null && <div className="text-warn" style={{ fontSize: 11.5 }}>{row.rejection_reason}</div>}
        </>
      ),
    },
  ];

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Live overview</h1>
          <div className="page-header__spec">§3.2 A · metrics refresh over WebSocket and REST polling</div>
        </div>
        <div className="data-clock">
          {isMock && <span className="mock-note">mock values</span>}
          {resyncToken > 0 && <span className="text-warn">resync #{resyncToken}</span>}
        </div>
      </header>

      {kpis.error !== null && kpis.data === null && <ErrorState error={kpis.error} context="Today's KPI cards could not be loaded." onRetry={kpis.refresh} />}
      {kpis.data === null && kpis.error === null && <LoadingState label="Loading KPI cards…" />}
      {kpis.data !== null && <KpiGrid snapshot={kpis.data} mock={isMock} />}

      {ribbon !== null && (
        <Panel title="Data freshness" subtitle="§7.1 quote staleness gate · §3.2 A ribbon detail">
          <FreshnessMeter freshness={ribbon.data_freshness} />
        </Panel>
      )}

      <Panel
        title="Order intents awaiting approval"
        subtitle="§9.2 step 6 · every §3.1 confirmable control requires a typed confirmation"
        mock={isMock}
        footnote={
          approvalQueue.length === 0
            ? 'Nothing is waiting. Under approval_required mode every risk-approved intent stops here until an operator acts on it.'
            : `${approvalQueue.length} intent(s) queued. Under constrained automated execution this queue is bypassed, and the decision trail records that no human clicked (§19.4).`
        }
      >
        {signals.data === null ? (
          <LoadingState label="Loading signals…" />
        ) : approvalQueue.length === 0 ? (
          <div className="empty-state">
            <strong>No intents waiting for approval</strong>
            <span>Active signals, including ones the risk engine rejected, are listed below.</span>
          </div>
        ) : (
          <div className="grid" style={{ gap: 12 }}>
            {approvalQueue.map((signal) => (
              <div key={signal.decision_id} className="disclosure">
                <div className="data-clock">
                  <strong>
                    {signal.symbol} {signal.side}
                  </strong>
                  <Badge tone={toneForVerdict(signal.risk_verdict ?? 'defer')}>{signal.risk_verdict === null ? 'no verdict' : VERDICT_LABELS[signal.risk_verdict]}</Badge>
                  <span className="text-faint">
                    quant {formatNumber(signal.quant_score, 3)} → final {signal.final_score === null ? '—' : formatNumber(signal.final_score, 3)} · research{' '}
                    {RESEARCH_STATUS_LABELS[signal.research_status]}
                  </span>
                  <span className="text-faint text-mono" style={{ fontSize: 11 }}>
                    {signal.decision_id}
                  </span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <ApproveRejectIntentControls signal={signal} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel
        title="Open positions"
        subtitle={`${positions.data?.length ?? 0} open · §3.2 A`}
        mock={isMock}
        flush
        footnote="A position is not considered protected until its stop is confirmed resting server-side at the broker (§16.2.4)."
      >
        {positions.error !== null && positions.data === null ? (
          <ErrorState error={positions.error} context="Open positions could not be loaded." onRetry={positions.refresh} />
        ) : (
          <DataTable
            rows={positions.data ?? []}
            columns={positionColumns}
            rowKey={(row) => row.position_ticket}
            initialSort={{ key: 'pnl', direction: 'desc' }}
            emptyTitle="No open positions"
            emptyMessage="Flat is a valid state."
            rowTone={(row) => (row.risk_status === 'unprotected_stop' ? 'danger' : row.risk_status === 'warning' ? 'warn' : null)}
          />
        )}
      </Panel>

      <Panel title="Working orders" subtitle="§3.2 A · cancellation requires typed confirmation" mock={isMock} flush>
        <DataTable rows={orders.data ?? []} columns={orderColumns} rowKey={(row) => row.order_ticket} initialSort={{ key: 'age', direction: 'desc' }} emptyTitle="No working orders" />
      </Panel>

      <Panel
        title="Recent closed trades"
        subtitle="§3.2 A · click a trade for the full decision trail"
        mock={isMock}
        flush
        actions={
          <CsvExportButton
            rows={recentTrades}
            filename="poseidon-recent-closed-trades.csv"
            columns={[
              { header: 'completed_trade_id', value: (row) => row.completed_trade_id },
              { header: 'symbol', value: (row) => row.symbol },
              { header: 'direction', value: (row) => row.direction },
              { header: 'entry_timestamp', value: (row) => row.entry_timestamp },
              { header: 'exit_timestamp', value: (row) => row.exit_timestamp },
              { header: 'net_realized_pnl', value: (row) => row.net_realized_pnl },
              { header: 'r_multiple', value: (row) => row.r_multiple },
              { header: 'outcome', value: (row) => row.outcome },
              { header: 'exit_reason', value: (row) => EXIT_REASON_LABELS[row.exit_reason] },
              { header: 'strategy_version', value: (row) => row.strategy_version },
            ]}
          />
        }
      >
        <DataTable
          rows={recentTrades}
          columns={tradeColumns}
          rowKey={(row) => row.completed_trade_id}
          initialSort={{ key: 'times', direction: 'desc' }}
          emptyTitle="No completed trades yet"
          emptyMessage="Today's ledger is empty."
        />
      </Panel>

      <Panel title="Active signals" subtitle="§3.2 A · quantified score, research status, risk verdict and rejection reason" mock={isMock} flush>
        <DataTable rows={signals.data ?? []} columns={signalColumns} rowKey={(row) => row.decision_id} initialSort={{ key: 'score', direction: 'desc' }} emptyTitle="No active signals" />
      </Panel>

      <div className="grid grid--2">
        <SystemEventList events={events.data ?? []} maxRows={8} mock={isMock} subtitle="most recent 8 of the loaded window" />
        <Panel title="Where to look next" subtitle="dashboard map">
          <ul className="consequences" style={{ paddingLeft: 18 }}>
            <li>
              <Link to="/trades">Trades & analytics</Link> — filters, CSV export, the required §3.2 B charts and per-trade decision trails.
            </li>
            <li>
              <Link to="/market">Live market & signal monitor</Link> — quotes, spread and quote age, bars, markers and the explainable signal breakdown.
            </li>
            <li>
              <Link to="/risk">Risk center</Link> — configured limits with utilization, halt controls, allow lists and the risk-config audit log.
            </li>
            <li>
              <Link to="/research">Strategy & AI research</Link> — strategy eligibility, feature freshness and research assessments with evidence and cost.
            </li>
            <li>
              <Link to="/backtests">Backtests & forward tests</Link> — run parameters, version hashes, §9.3 metrics and quant-only vs quant-plus-research.
            </li>
            <li>
              <Link to="/operations">Operations</Link> — bridge and reconciliation status, readiness, scheduler, data health, halt and execution-mode controls.
            </li>
          </ul>
        </Panel>
      </div>
    </>
  );
}
